import { Link } from 'react-router-dom';
import usePageMeta from '@/hooks/usePageMeta';
import { GENRES } from '@/mocks/manga';

const PRINCIPLES = [
  { icon: 'ri-leaf-line', title: '内容健康', desc: '坚持积极向上的内容导向，拒绝低俗、暴力与侵权内容，营造清朗的阅读环境。' },
  { icon: 'ri-flashlight-fill', title: '更新及时', desc: '紧跟连载进度，热门作品优先更新，让读者第一时间看到新章节，追更不掉队。' },
  { icon: 'ri-focus-3-line', title: '分类清晰', desc: '以标签为核心的分类体系，热血、搞笑、少女漫、少年漫、完结、汉化组一目了然。' },
  { icon: 'ri-hand-heart-line', title: '尊重创作者', desc: '支持正版、尊重每一位创作者与汉化组的辛勤付出，共同维护良好的内容生态。' },
];

const VALUES = [
  { icon: 'ri-user-heart-line', title: '读者优先', desc: '每一次筛选、排序与更新，都以帮助读者更快找到想看的内容为出发点。' },
  { icon: 'ri-compass-3-line', title: '持续进化', desc: '根据读者的反馈不断优化标签体系与阅读体验，让找漫画这件事越来越简单。' },
  { icon: 'ri-community-line', title: '共建社区', desc: '鼓励读者分享推荐、参与讨论，一起把心仪的作品分享给更多同好。' },
];

export default function AboutPage() {
  usePageMeta({
    title: '关于我们 - 免费51漫画',
    description:
      '了解免费51漫画：我们致力于按类型标签分类，提供热血类、搞笑类、少女漫、少年漫、完结作品、汉化组等优质漫画内容，让找漫画与追更更简单。',
    path: '/about',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'AboutPage',
        name: '关于免费51漫画',
        url: 'https://shonenx.github.io/about',
        description: '免费51漫画致力于按类型标签分类，提供优质漫画内容与便捷的追更体验。',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: '免费51漫画',
        url: 'https://shonenx.github.io/',
        email: 'contact@free51manga.example',
        description: '免费51漫画按类型标签分类，热血类、搞笑类、少女漫、少年漫、完结作品、汉化组一应俱全。',
      },
    ],
  });

  return (
    <main className="w-full bg-background-50">
      <section className="border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-7xl px-4 py-12 text-center md:px-6 md:py-16">
          <nav className="text-sm text-foreground-600" aria-label="面包屑">
            <ol className="flex items-center justify-center gap-1.5">
              <li><Link to="/" className="hover:text-primary-600">首页</Link></li>
              <li><i className="ri-arrow-right-s-line text-foreground-400"></i></li>
              <li aria-current="page" className="font-medium text-foreground-900">关于我们</li>
            </ol>
          </nav>
          <span className="mt-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-primary-500 text-3xl font-black text-background-50">
            51
          </span>
          <h1 className="mt-5 font-heading text-2xl font-black text-foreground-950 md:text-4xl">
            关于免费51漫画
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-sm leading-relaxed text-foreground-700 md:text-base">
            免费51漫画是一个以类型标签为核心的在线漫画阅读平台。我们相信，找漫画不该是一件麻烦事——清晰的分类、稳定的更新和顺畅的阅读体验，就是我们要做的事。
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-2">
          <div>
            <span className="text-sm font-semibold uppercase tracking-widest text-primary-600">Who We Are</span>
            <h2 className="mt-2 font-heading text-2xl font-black text-foreground-950">我们的定位</h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-700">
              从热血到搞笑，从少女漫到少年漫，从完结作品到汉化组佳作，我们用一套清晰易用的标签体系，把海量漫画内容组织得井井有条。无论你是想按类型挑一部新作，还是追着连载更新不放，都能在这里快速达成目标。
            </p>
            <p className="mt-4 text-sm leading-relaxed text-foreground-700">
              我们不追逐眼花缭乱的噱头，只专注把「找漫画、看漫画、追更新」这几件事做扎实。让每一次点击都有意义，让每一位读者都能满载而归。
            </p>
          </div>
          <div>
            <span className="text-sm font-semibold uppercase tracking-widest text-accent-600">Content Principles</span>
            <h2 className="mt-2 font-heading text-2xl font-black text-foreground-950">内容原则</h2>
            <ul className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {PRINCIPLES.map((p) => (
                <li key={p.title} className="rounded-lg border border-background-200 bg-background-100 p-4">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-secondary-100 text-lg text-primary-600">
                    <i className={p.icon}></i>
                  </span>
                  <h3 className="mt-3 text-sm font-bold text-foreground-900">{p.title}</h3>
                  <p className="mt-1.5 text-xs leading-relaxed text-foreground-700">{p.desc}</p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="bg-background-100 py-12 md:py-16">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <div className="text-center">
            <span className="text-sm font-semibold uppercase tracking-widest text-primary-600">Our Philosophy</span>
            <h2 className="mt-2 font-heading text-2xl font-black text-foreground-950">发展理念</h2>
            <p className="mx-auto mt-3 max-w-xl text-sm text-foreground-600">
              一路走来，我们始终相信三件事。
            </p>
          </div>
          <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-3">
            {VALUES.map((v, i) => (
              <div key={v.title} className="relative rounded-lg border border-background-200 bg-background-50 p-6">
                <span className="absolute right-4 top-4 text-4xl font-black text-background-200">0{i + 1}</span>
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-500 text-xl text-background-50">
                  <i className={v.icon}></i>
                </span>
                <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">{v.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-700">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-2">
          <div>
            <span className="text-sm font-semibold uppercase tracking-widest text-accent-600">Word of Mouth</span>
            <h2 className="mt-2 font-heading text-2xl font-black text-foreground-950">读者口碑</h2>
            <div className="mt-5 space-y-3">
              {[
                '“标签分类清晰，找热血漫画基本一步到位。” —— 夜风',
                '“完结作品专区太友好了，可以一次追完。” —— 柚子',
                '“汉化组专区内容很精品，翻译稳定。” —— 阿哲',
                '“界面干净，按标签筛选又快又准。” —— 小满',
              ].map((t) => (
                <p key={t} className="flex items-start gap-2 rounded-lg border border-background-200 bg-background-100 p-4 text-sm text-foreground-700">
                  <i className="ri-double-quotes-l mt-0.5 text-primary-500"></i>
                  <span>{t}</span>
                </p>
              ))}
            </div>
          </div>
          <div>
            <span className="text-sm font-semibold uppercase tracking-widest text-primary-600">Get In Touch</span>
            <h2 className="mt-2 font-heading text-2xl font-black text-foreground-950">联系我们</h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-700">
              无论是内容建议、版权相关事宜，还是合作洽谈，欢迎随时与我们联系，我们会认真对待每一条来信。
            </p>
            <div className="mt-5 grid grid-cols-1 gap-3">
              <a
                href="mailto:contact@free51manga.example"
                className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-100 p-4 text-sm text-foreground-800 transition-colors hover:border-primary-300"
              >
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-secondary-100 text-lg text-primary-600">
                  <i className="ri-mail-line"></i>
                </span>
                <span>
                  <span className="block font-bold">商务与合作</span>
                  <span className="text-foreground-600">contact@free51manga.example</span>
                </span>
              </a>
              <a
                href="mailto:support@free51manga.example"
                className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-100 p-4 text-sm text-foreground-800 transition-colors hover:border-primary-300"
              >
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-secondary-100 text-lg text-primary-600">
                  <i className="ri-service-line"></i>
                </span>
                <span>
                  <span className="block font-bold">用户反馈</span>
                  <span className="text-foreground-600">support@free51manga.example</span>
                </span>
              </a>
              <div className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-100 p-4 text-sm text-foreground-800">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-secondary-100 text-lg text-primary-600">
                  <i className="ri-community-line"></i>
                </span>
                <span>
                  <span className="block font-bold">内容分类</span>
                  <span className="flex flex-wrap gap-1.5 pt-1">
                    {GENRES.slice(0, 6).map((g) => (
                      <Link key={g.id} to={`/category/${g.id}`} className="rounded-md bg-secondary-100 px-2 py-0.5 text-xs text-secondary-800 hover:bg-secondary-200">
                        {g.name}
                      </Link>
                    ))}
                  </span>
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}